import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType, ImageRun } from "docx";
import riLogoUrl from "@/shared/assets/ri_logo_horizontal.png";
import { getNumeration } from "./numbering";
import type { TemplateField } from "./treeHelpers";

/**
 * Creates and downloads a Word Document (.docx) for the given template schema.
 */
export async function exportTemplateToDocx(schemaDefinition: any, projectName: string) {
  let fields: TemplateField[] = [];
  if (typeof schemaDefinition === "string") {
    try {
      fields = JSON.parse(schemaDefinition);
    } catch (e) {
      console.error("Failed to parse schema definition", e);
    }
  } else {
    fields = schemaDefinition as TemplateField[];
  }

  let logoData: Uint8Array | undefined;
  try {
    const res = await fetch(riLogoUrl);
    if (res.ok) {
      const arrayBuffer = await res.arrayBuffer();
      if (arrayBuffer.byteLength > 0) {
        logoData = new Uint8Array(arrayBuffer);
      } else {
        console.warn("Logo fetch returned empty body");
      }
    } else {
      console.warn("Logo fetch failed with status:", res.status);
    }
  } catch (e) {
    console.error("Failed to load logo", e);
  }

  const paragraphs: (Paragraph | Table)[] = [];

  if (logoData) {
    paragraphs.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE, size: 0, color: "000000" },
          bottom: { style: BorderStyle.NONE, size: 0, color: "000000" },
          left: { style: BorderStyle.NONE, size: 0, color: "000000" },
          right: { style: BorderStyle.NONE, size: 0, color: "000000" },
          insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "000000" },
          insideVertical: { style: BorderStyle.NONE, size: 0, color: "000000" }
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                shading: { fill: "000000" },
                margins: { top: 400, bottom: 400, left: 400, right: 400 },
                children: [
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: logoData,
                        transformation: { width: 200, height: 55 },
                        type: "png"
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );
  }

  // Golden Title with bottom border
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `${projectName} - Site Survey Template`,
          color: "F4B41A",
          size: 36, // 18pt
          bold: true,
        }),
      ],
      border: {
        bottom: {
          color: "F4B41A",
          space: 10,
          style: BorderStyle.SINGLE,
          size: 12,
        },
      },
      spacing: { before: logoData ? 400 : 0, after: 400 },
    })
  );

  function processTemplateField(field: TemplateField, pathIndices: number[]) {
    const depth = pathIndices.length - 1;
    const index = pathIndices[depth];
    const numeration = getNumeration(depth, index);
    const titleWithNum = `${numeration} ${field.title}`;

    // Treat any field with nested children as a section/node,
    // regardless of its `type` value — handles edge cases where
    // grouping fields may not have type === "node"
    const hasChildren = field.fields && field.fields.length > 0;

    if (hasChildren) {
      // Node / Section heading
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: titleWithNum,
              color: "F4B41A",
              bold: true,
            }),
          ],
          heading: depth === 0 ? HeadingLevel.HEADING_2
                 : depth === 1 ? HeadingLevel.HEADING_3
                 : HeadingLevel.HEADING_4,
          spacing: { before: 200, after: 100 },
        })
      );
      field.fields!.forEach((child, idx) => {
        processTemplateField(child, [...pathIndices, idx]);
      });
    } else {
      // Question (Input, Select, Photos, etc.)
      const isSelect = field.type === "select" || field.type === "multiSelect";

      paragraphs.push(
        new Paragraph({
          spacing: { before: 100, after: isSelect ? 50 : 150 },
          indent: { left: depth * 360 },
          children: [
            new TextRun({
              text: titleWithNum,
              bold: true,
            }),
            new TextRun({
              text: field.required ? " *" : "",
              color: "FF0000",
            }),
          ],
        })
      );

      if (isSelect && field.options && field.options.length > 0) {
        for (const option of field.options) {
          paragraphs.push(
            new Paragraph({
              text: option,
              bullet: { level: 0 },
              spacing: { after: 50 },
              indent: { left: (depth + 1) * 360 },
            })
          );
        }
        paragraphs.push(new Paragraph({ spacing: { after: 150 } })); // extra spacing after options
      }
    }
  }

  fields.forEach((field, idx) => {
    processTemplateField(field, [idx]);
  });

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: paragraphs,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${projectName.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_survey_template.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
